import { useMemo } from "react";
import styled from "styled-components";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { Label } from "@sablier/v2-components/molecules";
import { Position } from "@sablier/v2-components/organisms";
import { useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import { Card as Base } from "~/client/components/molecules";
import { useStreamCurrent, useToken } from "~/client/hooks";
import type { ComponentProps } from "react";

const WrapperPartial = styled(Base)`
  width: 100%;
  *[data-component="card"] {
    border-radius: 8px;
  }
`;

const Content = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    row-gap: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
    width: 100%;

    *[data-component="label"] {
      color: ${(props) => props.theme.colors.gray100};
    }
  }
`;

const Explanation = styled.div`
  width: 100%;
  & > p {
    ${(props) => props.theme.styles.textParagraph}
  }
`;

const Wrapper = styled(WrapperPartial)`
  ${(props) => props.theme.medias.maxSM} {
    gap: calc(${(props) => props.theme.sizes.edge} * 3 / 4);
    ${Explanation} {
      & > p {
        line-height: 20pt;
      }
    }
  }
`;

interface Props {
  isStale: boolean;
}

function Summary({ isStale = false }: Props) {
  const { stream } = useStreamCurrent();
  const token = useToken({ token: stream?.token });
  const { t } = useT();

  const items = useMemo(() => {
    const list: ComponentProps<typeof Position>["items"] = [
      {
        label: {
          value: `${t("words.streamed")}:`,
        },
        progress: {
          percentage: stream?.streamedAmountPercentage?.toNumber(),
        },
        amount: {
          token,
          value: stream?.streamedAmount.humanized.toString(),
        },
      },
      {
        label: {
          value: `${t("words.nonReturnable")}:`,
        },
        progress: {
          percentage: stream?.returnableAmountPercentage?.toNumber(),
        },
        amount: {
          token,
          value: stream?.returnableAmount.humanized.toString(),
        },
      },
      {
        label: {
          value: `${_.capitalize(t("words.deposited"))}:`,
        },
        amount: {
          token,
          value: stream?.depositAmount.humanized.toString(),
          isFormatted: true,
        },
        percentage: 100,
      },
    ];

    if (isStale) {
      list.splice(1, 1);
    }

    return list;
  }, [isStale, stream, token, t]);

  return (
    <Wrapper isTopHidden>
      <Content>
        <Label
          value={t("modalCovers.renounce.summary")}
          icon={InformationCircleIcon}
          isIconLast
        />
        <Explanation>
          <p>{t("modalCovers.renounce.description")}</p>
        </Explanation>
        <Position items={items} />
      </Content>
    </Wrapper>
  );
}

export default Summary;
